import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { Message } from 'element-ui';

/**
 * 带有解包响应体泛型能力的自定义 Axios 接口
 */
export interface CustomAxiosInstance extends AxiosInstance {
  request<T = any, R = T, D = any>(config: AxiosRequestConfig<D>): Promise<R>;
  get<T = any, R = T, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<R>;
  delete<T = any, R = T, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<R>;
  head<T = any, R = T, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<R>;
  options<T = any, R = T, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<R>;
  post<T = any, R = T, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R>;
  put<T = any, R = T, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R>;
  patch<T = any, R = T, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R>;
}

const instance = axios.create({
  baseURL: '/api'
}) as CustomAxiosInstance;

// 请求拦截器
instance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const url = config.url || '';
    if (url.includes('http://') || url.includes('https://')) {
      return config;
    }

    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
    if (token) {
      config.headers.set('token', token);
    }
    return config;
  },
  (error: any) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
instance.interceptors.response.use(
  (response: AxiosResponse) => {
    const url = response.config.url || '';
    const contentType = response.headers['content-type'] || '';

    // 非 JSON 响应（如文件下载、二进制流），直接返回原始 response
    if (!contentType.includes('application/json')) {
      return response as any;
    }

    if (!url.includes('http://') && !url.includes('https://')) {
      const res = response.data;
      if (res && res.success === false) {
        Message.error(res.message || res.msg || '操作失败');
        return Promise.reject(response);
      }
    }

    return response.data;
  },
  (err: any) => {
    if (err && err.response && (err.response.status === 200 || err.response.status === 302)) {
      return Promise.reject(err);
    }

    let needLogin = false;
    if (err && err.response) {
      if (err.response.data?.data?.needLogin) {
        needLogin = true;
      }
      switch (err.response.status) {
        case 400:
          err.message = '请求错误(400)';
          break;
        case 401:
          err.message = needLogin ? '会话已超时或已被踢出，请重新登录(401)' : '权限不足(401)';
          if (needLogin) {
            try {
              localStorage.removeItem('token');
              if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
                window.dispatchEvent(new CustomEvent('websocket-auth-failed', {
                  detail: { code: 401, reason: err.message }
                }));
              }
            } catch (_) {}
          }
          break;
        case 403:
          err.message = `${err.response.data?.message || '访问被拒绝'}(403)`;
          break;
        case 404:
          err.message = '请求出错(404)';
          break;
        case 408:
          err.message = '请求超时(408)';
          break;
        case 500:
          err.message = '服务器错误(500)';
          break;
        case 501:
          err.message = '服务未实现(501)';
          break;
        case 502:
          err.message = '网络错误(502)';
          break;
        case 503:
          err.message = '服务不可用(503)';
          break;
        case 504:
          err.message = '网络超时(504)';
          break;
        case 505:
          err.message = 'HTTP版本不受支持(505)';
          break;
        default:
          err.message = `连接出错(${err.response.status})!`;
      }
    } else {
      err.message = '连接服务器失败!';
    }

    Message.error(err.message);
    return Promise.reject(err);
  }
);

export default instance;
